package br.com.vvrefeicoes.portal.data.repository

import br.com.vvrefeicoes.portal.domain.model.MonthPeriod
import br.com.vvrefeicoes.portal.domain.model.PurchasesExtraAnalytics
import br.com.vvrefeicoes.portal.domain.model.PurchasesExtraDirectorate
import br.com.vvrefeicoes.portal.domain.model.PurchasesExtraDirectorateMonth
import br.com.vvrefeicoes.portal.domain.model.PurchasesExtraDirectorateUnit
import br.com.vvrefeicoes.portal.domain.model.PurchasesExtraReasonCount
import br.com.vvrefeicoes.portal.domain.model.PurchasesImportMode
import br.com.vvrefeicoes.portal.domain.model.PurchasesImportPayload
import br.com.vvrefeicoes.portal.domain.model.PurchasesImportPreview
import br.com.vvrefeicoes.portal.domain.model.PurchasesImportResult
import br.com.vvrefeicoes.portal.domain.model.PurchasesPanelCategory
import br.com.vvrefeicoes.portal.domain.model.PurchasesPanelCategoryMonth
import br.com.vvrefeicoes.portal.domain.model.PurchasesPanelData
import br.com.vvrefeicoes.portal.domain.model.PurchasesPanelMonthlyCost
import br.com.vvrefeicoes.portal.domain.model.PurchasesPanelMonthlyCount
import br.com.vvrefeicoes.portal.domain.model.PurchasesPanelPeriodMonthlyCost
import br.com.vvrefeicoes.portal.domain.model.PurchasesPanelProductChange
import br.com.vvrefeicoes.portal.domain.model.PurchasesPanelProductDetail
import br.com.vvrefeicoes.portal.domain.model.PurchasesPanelProductMonthPrice
import br.com.vvrefeicoes.portal.domain.model.PurchasesPanelProductUnitPriceChange
import br.com.vvrefeicoes.portal.domain.model.PurchasesProductRow
import br.com.vvrefeicoes.portal.domain.model.PurchasesProvision
import br.com.vvrefeicoes.portal.domain.model.PurchasesSummaryLine
import br.com.vvrefeicoes.portal.domain.model.PurchasesType
import br.com.vvrefeicoes.portal.domain.repository.AuthRepository
import br.com.vvrefeicoes.portal.domain.repository.PurchasesRepository
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.HttpRequestBuilder
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.post
import io.ktor.client.request.put
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.HttpStatusCode
import io.ktor.http.contentType
import io.ktor.http.isSuccess
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

class RemotePurchasesRepository(
    private val httpClient: HttpClient,
    private val baseUrl: String,
    private val authRepository: AuthRepository,
) : PurchasesRepository {
    override suspend fun load(
        type: PurchasesType,
        period: MonthPeriod,
    ): List<PurchasesProductRow> {
        val response =
            httpClient.get(
                "$baseUrl/purchases/entries?type=${type.name}&year=${period.year}&month=${period.month}",
            ) {
                authorize()
            }
        ensureSuccess(response)
        return response.body<List<PurchasesEntryDto>>().map { it.toDomain() }
    }

    override suspend fun summary(period: MonthPeriod): List<PurchasesSummaryLine> {
        val response =
            httpClient.get("$baseUrl/purchases/entries/summary?year=${period.year}&month=${period.month}") {
                authorize()
            }
        ensureSuccess(response)
        return response.body<List<PurchasesSummaryDto>>().map { it.toDomain() }
    }

    override suspend fun panel(
        start: MonthPeriod,
        end: MonthPeriod,
    ): PurchasesPanelData {
        val response =
            httpClient.get(
                "$baseUrl/purchases/panel?startYear=${start.year}&startMonth=${start.month}" +
                    "&endYear=${end.year}&endMonth=${end.month}",
            ) {
                authorize()
            }
        ensureSuccess(response)
        return response.body<PurchasesPanelDto>().toDomain()
    }

    override suspend fun provision(period: MonthPeriod): PurchasesProvision {
        val response =
            httpClient.get("$baseUrl/purchases/provisions?year=${period.year}&month=${period.month}") {
                authorize()
            }
        ensureSuccess(response)
        return response.body<PurchasesProvisionDto>().toDomain()
    }

    override suspend fun saveProvision(
        period: MonthPeriod,
        amountCents: Long,
    ): PurchasesProvision {
        val response =
            httpClient.put("$baseUrl/purchases/provisions?year=${period.year}&month=${period.month}") {
                authorize()
                contentType(ContentType.Application.Json)
                setBody(PurchasesProvisionUpdateDto(amountCents))
            }
        ensureSuccess(response)
        return response.body<PurchasesProvisionDto>().toDomain()
    }

    override suspend fun latestPeriod(): MonthPeriod? {
        val response = httpClient.get("$baseUrl/purchases/panel/latest-period") { authorize() }
        ensureSuccess(response)
        if (response.status == HttpStatusCode.NoContent) {
            return null
        }
        val dto = response.body<PurchasesLatestPeriodDto>()
        return MonthPeriod(dto.year, dto.month)
    }

    override suspend fun update(row: PurchasesProductRow): PurchasesProductRow {
        val response =
            httpClient.put("$baseUrl/purchases/entries/${row.id}") {
                authorize()
                contentType(ContentType.Application.Json)
                setBody(
                    PurchasesEntryUpdateDto(
                        name = row.name,
                        measurement = row.measurement,
                        consumption = row.consumption,
                        unitPriceCents = row.unitPriceCents,
                        totalCents = row.totalCents,
                    ),
                )
            }
        ensureSuccess(response)
        return response.body<PurchasesEntryDto>().toDomain()
    }

    override suspend fun previewImport(payload: PurchasesImportPayload): PurchasesImportPreview {
        val response =
            httpClient.post("$baseUrl/purchases/entries/import/preview") {
                authorize()
                contentType(ContentType.Application.Json)
                setBody(payload.toDto())
            }
        ensureSuccess(response)
        return response.body<PurchasesImportPreviewDto>().toDomain()
    }

    override suspend fun importEntries(payload: PurchasesImportPayload): PurchasesImportResult {
        val response =
            httpClient.post("$baseUrl/purchases/entries/import") {
                authorize()
                contentType(ContentType.Application.Json)
                setBody(payload.toDto())
            }
        ensureSuccess(response)
        return response.body<PurchasesImportResultDto>().toDomain()
    }

    private suspend fun ensureSuccess(response: HttpResponse) {
        if (!response.status.isSuccess()) {
            val message =
                runCatching {
                    Json { ignoreUnknownKeys = true }
                        .decodeFromString<PurchasesApiErrorDto>(response.bodyAsText())
                        .message
                }.getOrNull()
            error(message ?: "HTTP ${response.status.value}")
        }
    }

    private fun HttpRequestBuilder.authorize() {
        authRepository.session.value?.token?.let { token ->
            header(HttpHeaders.Authorization, "Bearer $token")
        }
    }
}

@Serializable
private data class PurchasesEntryDto(
    val id: String,
    val type: PurchasesType,
    val year: Int,
    val month: Int,
    val name: String,
    val measurement: String,
    val consumption: Long,
    val unitPriceCents: Long,
    val totalCents: Long,
    val sortOrder: Int = 0,
) {
    fun toDomain() =
        PurchasesProductRow(
            id = id,
            name = name,
            measurement = measurement,
            consumption = consumption,
            unitPriceCents = unitPriceCents,
            totalCents = totalCents,
            sortOrder = sortOrder,
        )
}

@Serializable
private data class PurchasesSummaryDto(
    val type: PurchasesType,
    val totalCents: Long,
    val productCount: Long,
) {
    fun toDomain() = PurchasesSummaryLine(type = type, totalCents = totalCents, productCount = productCount)
}

@Serializable
private data class PurchasesLatestPeriodDto(
    val year: Int,
    val month: Int,
)

@Serializable
private data class PurchasesProvisionDto(
    val year: Int,
    val month: Int,
    val amountCents: Long = 0,
    val configured: Boolean = false,
) {
    fun toDomain() = PurchasesProvision(year, month, amountCents, configured)
}

@Serializable
private data class PurchasesProvisionUpdateDto(
    val amountCents: Long,
)

@Serializable
private data class PurchasesPanelDto(
    val totalCents: Long,
    val previousTotalCents: Long,
    val plannedTotalCents: Long = 0,
    val categories: List<PurchasesPanelCategoryDto> = emptyList(),
    val extrasCount: Long = 0,
    val previousExtrasCount: Long = 0,
    val annualCosts: List<PurchasesPanelMonthlyCostDto> = emptyList(),
    val annualProvisions: List<PurchasesPanelMonthlyCostDto> = emptyList(),
    val periodProvisions: List<PurchasesPanelPeriodMonthlyCostDto> = emptyList(),
    val extrasEvolution: List<PurchasesPanelMonthlyCountDto> = emptyList(),
    val topIncreases: List<PurchasesPanelProductChangeDto> = emptyList(),
    val topReductions: List<PurchasesPanelProductChangeDto> = emptyList(),
    val extras: PurchasesExtrasAnalyticsDto? = null,
) {
    fun toDomain() =
        PurchasesPanelData(
            totalCents = totalCents,
            previousTotalCents = previousTotalCents,
            plannedTotalCents = plannedTotalCents,
            categories = categories.map { it.toDomain() },
            extrasCount = extrasCount,
            previousExtrasCount = previousExtrasCount,
            annualCosts = annualCosts.map { it.toDomain() },
            annualProvisions = annualProvisions.map { it.toDomain() },
            periodProvisions = periodProvisions.map { it.toDomain() },
            extrasEvolution = extrasEvolution.map { it.toDomain() },
            topIncreases = topIncreases.map { it.toDomain() },
            topReductions = topReductions.map { it.toDomain() },
            extrasAnalytics = extras?.toDomain() ?: PurchasesExtraAnalytics(),
        )
}

@Serializable
private data class PurchasesExtrasAnalyticsDto(
    val months: List<PurchasesExtrasMonthDto> = emptyList(),
    val directorates: List<PurchasesExtrasDirectorateDto> = emptyList(),
    val reasons: List<PurchasesExtrasReasonDto> = emptyList(),
) {
    fun toDomain() =
        PurchasesExtraAnalytics(
            months = months.map { MonthPeriod(it.year, it.month) },
            directorates = directorates.map { it.toDomain() },
            reasons = reasons.map { it.toDomain() },
        )
}

@Serializable
private data class PurchasesExtrasMonthDto(
    val year: Int,
    val month: Int,
)

@Serializable
private data class PurchasesExtrasDirectorateDto(
    val name: String? = null,
    val orderCount: Long = 0,
    val months: List<PurchasesExtrasDirectorateMonthDto> = emptyList(),
    val units: List<PurchasesExtrasDirectorateUnitDto> = emptyList(),
) {
    fun toDomain() =
        PurchasesExtraDirectorate(
            name = name,
            orderCount = orderCount,
            months = months.map { it.toDomain() },
            units = units.map { it.toDomain() },
        )
}

@Serializable
private data class PurchasesExtrasDirectorateUnitDto(
    val name: String,
    val orderCount: Long = 0,
    val months: List<PurchasesExtrasDirectorateMonthDto> = emptyList(),
) {
    fun toDomain() =
        PurchasesExtraDirectorateUnit(
            name = name,
            orderCount = orderCount,
            months = months.map { it.toDomain() },
        )
}

@Serializable
private data class PurchasesExtrasDirectorateMonthDto(
    val year: Int,
    val month: Int,
    val orderCount: Long = 0,
) {
    fun toDomain() = PurchasesExtraDirectorateMonth(year = year, month = month, orderCount = orderCount)
}

@Serializable
private data class PurchasesExtrasReasonDto(
    val id: String? = null,
    val name: String,
    val active: Boolean = true,
    val orderCount: Long = 0,
) {
    fun toDomain() =
        PurchasesExtraReasonCount(
            id = id,
            name = name,
            active = active,
            orderCount = orderCount,
        )
}

@Serializable
private data class PurchasesPanelMonthlyCostDto(
    val month: Int,
    val totalCents: Long,
) {
    fun toDomain() = PurchasesPanelMonthlyCost(month = month, totalCents = totalCents)
}

@Serializable
private data class PurchasesPanelPeriodMonthlyCostDto(
    val year: Int,
    val month: Int,
    val totalCents: Long,
) {
    fun toDomain() = PurchasesPanelPeriodMonthlyCost(year = year, month = month, totalCents = totalCents)
}

@Serializable
private data class PurchasesPanelMonthlyCountDto(
    val month: Int,
    val orderCount: Long,
) {
    fun toDomain() = PurchasesPanelMonthlyCount(month = month, orderCount = orderCount)
}

@Serializable
private data class PurchasesPanelProductChangeDto(
    val type: PurchasesType,
    val productName: String,
    val measurement: String = "",
    val previousUnitPriceCents: Long,
    val currentUnitPriceCents: Long,
    val variationPercent: Double,
) {
    fun toDomain() =
        PurchasesPanelProductChange(
            type = type,
            productName = productName,
            measurement = measurement,
            previousUnitPriceCents = previousUnitPriceCents,
            currentUnitPriceCents = currentUnitPriceCents,
            variationPercent = variationPercent,
        )
}

@Serializable
private data class PurchasesPanelCategoryDto(
    val type: PurchasesType,
    val totalCents: Long,
    val previousTotalCents: Long,
    val productCount: Long = 0,
    val totalReductionsCents: Long = 0,
    val totalIncreasesCents: Long = 0,
    val topReductions: List<PurchasesPanelProductUnitPriceChangeDto> = emptyList(),
    val topIncreases: List<PurchasesPanelProductUnitPriceChangeDto> = emptyList(),
    val monthly: List<PurchasesPanelCategoryMonthDto> = emptyList(),
    val details: List<PurchasesPanelProductDetailDto> = emptyList(),
) {
    fun toDomain() =
        PurchasesPanelCategory(
            type = type,
            totalCents = totalCents,
            previousTotalCents = previousTotalCents,
            productCount = productCount,
            totalReductionsCents = totalReductionsCents,
            totalIncreasesCents = totalIncreasesCents,
            topReductions = topReductions.map { it.toDomain() },
            topIncreases = topIncreases.map { it.toDomain() },
            monthly = monthly.map { it.toDomain() },
            details = details.map { it.toDomain() },
        )
}

@Serializable
private data class PurchasesPanelCategoryMonthDto(
    val year: Int,
    val month: Int,
    val totalCents: Long,
    val variationPercent: Double? = null,
) {
    fun toDomain() =
        PurchasesPanelCategoryMonth(
            year = year,
            month = month,
            totalCents = totalCents,
            variationPercent = variationPercent,
        )
}

@Serializable
private data class PurchasesPanelProductUnitPriceChangeDto(
    val type: PurchasesType,
    val productName: String,
    val previousUnitPriceCents: Long,
    val currentUnitPriceCents: Long,
    val previousTotalCents: Long = 0,
    val currentTotalCents: Long = 0,
    val changeCents: Long,
    val variationPercent: Double,
    val totalChangeCents: Long = changeCents,
    val totalVariationPercent: Double = variationPercent,
    val averageUnitPriceCents: Long = 0,
    val monthly: List<PurchasesPanelProductMonthPriceDto> = emptyList(),
) {
    fun toDomain() =
        PurchasesPanelProductUnitPriceChange(
            type = type,
            productName = productName,
            previousUnitPriceCents = previousUnitPriceCents,
            currentUnitPriceCents = currentUnitPriceCents,
            previousTotalCents = previousTotalCents,
            currentTotalCents = currentTotalCents,
            changeCents = changeCents,
            variationPercent = variationPercent,
            totalChangeCents = totalChangeCents,
            totalVariationPercent = totalVariationPercent,
            averageUnitPriceCents = averageUnitPriceCents,
            monthly = monthly.map { it.toDomain() },
        )
}

@Serializable
private data class PurchasesPanelProductDetailDto(
    val type: PurchasesType,
    val productName: String,
    val previousUnitPriceCents: Long? = null,
    val currentUnitPriceCents: Long? = null,
    val changeCents: Long? = null,
    val variationPercent: Double? = null,
    val averageUnitPriceCents: Long = 0,
    val monthly: List<PurchasesPanelProductMonthPriceDto> = emptyList(),
) {
    fun toDomain() =
        PurchasesPanelProductDetail(
            type = type,
            productName = productName,
            previousUnitPriceCents = previousUnitPriceCents,
            currentUnitPriceCents = currentUnitPriceCents,
            changeCents = changeCents,
            variationPercent = variationPercent,
            averageUnitPriceCents = averageUnitPriceCents,
            monthly = monthly.map { it.toDomain() },
        )
}

@Serializable
private data class PurchasesPanelProductMonthPriceDto(
    val year: Int,
    val month: Int,
    val unitPriceCents: Long,
    val totalCents: Long = 0,
    val changeCents: Long? = null,
    val variationPercent: Double? = null,
) {
    fun toDomain() =
        PurchasesPanelProductMonthPrice(
            year = year,
            month = month,
            unitPriceCents = unitPriceCents,
            totalCents = totalCents,
            changeCents = changeCents,
            variationPercent = variationPercent,
        )
}

@Serializable
private data class PurchasesEntryUpdateDto(
    val name: String,
    val measurement: String,
    val consumption: Long,
    val unitPriceCents: Long,
    val totalCents: Long,
)

@Serializable
private data class PurchasesImportRequestDto(
    val type: PurchasesType,
    val year: Int,
    val lastMonth: Int,
    val targetMonth: Int,
    val products: List<PurchasesImportProductDto>,
)

@Serializable
private data class PurchasesImportProductDto(
    val name: String,
    val measurement: String,
    val consumption: String,
    val months: List<PurchasesImportMonthDto>,
)

@Serializable
private data class PurchasesImportMonthDto(
    val month: Int,
    val unitPrice: String,
    val totalCost: String,
)

@Serializable
private data class PurchasesImportPreviewDto(
    val type: PurchasesType,
    val year: Int,
    val lastMonth: Int,
    val targetMonth: Int,
    val mode: PurchasesImportMode,
    val yearHasEntries: Boolean,
    val productCount: Int,
    val rowCount: Int,
    val totalCents: Long,
    val canImport: Boolean,
) {
    fun toDomain() =
        PurchasesImportPreview(
            type = type,
            year = year,
            lastMonth = lastMonth,
            targetMonth = targetMonth,
            mode = mode,
            yearHasEntries = yearHasEntries,
            productCount = productCount,
            rowCount = rowCount,
            totalCents = totalCents,
            canImport = canImport,
        )
}

@Serializable
private data class PurchasesImportResultDto(
    val type: PurchasesType,
    val year: Int,
    val lastMonth: Int,
    val targetMonth: Int,
    val mode: PurchasesImportMode,
    val monthsImported: Int,
    val rowsInserted: Int,
    val rowsReplaced: Int,
    val productCount: Int,
    val totalCents: Long,
) {
    fun toDomain() =
        PurchasesImportResult(
            type = type,
            year = year,
            lastMonth = lastMonth,
            targetMonth = targetMonth,
            mode = mode,
            monthsImported = monthsImported,
            rowsInserted = rowsInserted,
            rowsReplaced = rowsReplaced,
            productCount = productCount,
            totalCents = totalCents,
        )
}

@Serializable
private data class PurchasesApiErrorDto(
    val message: String? = null,
)

private fun PurchasesImportPayload.toDto() =
    PurchasesImportRequestDto(
        type = type,
        year = year,
        lastMonth = lastMonth,
        targetMonth = targetMonth,
        products =
            products.map { product ->
                PurchasesImportProductDto(
                    name = product.name,
                    measurement = product.measurement,
                    consumption = product.consumption,
                    months =
                        product.months.map { month ->
                            PurchasesImportMonthDto(
                                month = month.month,
                                unitPrice = month.unitPrice,
                                totalCost = month.totalCost,
                            )
                        },
                )
            },
    )
